import { useEffect, useState } from 'react';

import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { differenceInMinutes, format } from 'date-fns';
import PropTypes from 'prop-types';

import dateParser from 'helpers/dateParser';
import { ORDER_STATUSES } from 'lib/orderStatuses';

const LABEL = 'Entregar antes de:';
const COMPACT_DATE_FORMAT = 'd/MM h:mm a';
const CLOSE_TO_DEADLINE_MINUTES = 120;
const REFRESH_INTERVAL_MILLISECONDS = 60 * 1000;
const FINISHED_STATUSES = [
  ORDER_STATUSES.delivered,
  ORDER_STATUSES.deliveryPickedUp,
  ORDER_STATUSES.canceled,
];

function ExpectedDeliveryDate({ deliverAt, status }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(
      () => setNow(new Date()),
      REFRESH_INTERVAL_MILLISECONDS,
    );
    return () => clearInterval(interval);
  }, []);

  const deadline = new Date(deliverAt);
  const fullDate = dateParser(deliverAt);
  const isFinished = FINISHED_STATUSES.includes(status?.value);
  const minutesLeft = differenceInMinutes(deadline, now);
  const isAlert = !isFinished && minutesLeft <= CLOSE_TO_DEADLINE_MINUTES;
  const isOverdue = minutesLeft < 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: '6px' }}>
      <Typography noWrap variant="caption" sx={{ fontWeight: 'bold' }}>
        {LABEL}
      </Typography>
      {isAlert ? (
        <Tooltip
          title={
            isOverdue
              ? `Venció la hora límite de entrega: ${fullDate}`
              : `Se acerca la hora límite de entrega: ${fullDate}`
          }
        >
          <Chip
            size="small"
            color={isOverdue ? 'error' : 'warning'}
            icon={
              isOverdue ? (
                <WarningAmberOutlinedIcon />
              ) : (
                <AccessTimeOutlinedIcon />
              )
            }
            label={format(deadline, COMPACT_DATE_FORMAT)}
            sx={{ alignSelf: 'flex-start', maxWidth: '100%', fontWeight: 600 }}
          />
        </Tooltip>
      ) : (
        <Typography noWrap variant="caption">
          {fullDate}
        </Typography>
      )}
    </Box>
  );
}

ExpectedDeliveryDate.propTypes = {
  deliverAt: PropTypes.string.isRequired,
  status: PropTypes.shape({ value: PropTypes.string }),
};

ExpectedDeliveryDate.defaultProps = {
  status: undefined,
};

export default ExpectedDeliveryDate;
